/**
 * Global error boundary component for Next.js App Router.
 * Catches errors in the root layout and displays a fallback UI.
 *
 * Note: This is a client component ('use client') and must be in the app directory.
 * It must include <html> and <body> tags as it replaces the root layout on error.
 * It catches errors that the regular error.tsx cannot catch (including in the root layout).
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/global-error
 */
'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ErrorPageLayout } from '@/components/error/error-page-layout';
import { sanitizeUserAgent } from '@/lib/validation';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log error to Sentry with enhanced context
    Sentry.captureException(error, {
      tags: {
        errorBoundary: 'global',
        source: 'client',
      },
      extra: {
        digest: error.digest,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: sanitizeUserAgent(
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined
        ),
      },
    });
  }, [error]);

  return (
    <html>
      <body>
        <ErrorPageLayout
          title="Something went wrong!"
          message="An unexpected error occurred."
          action={<Button onClick={reset}>Try again</Button>}
        />
      </body>
    </html>
  );
}
